import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { MeResponse, Permission } from '@insider/shared';
import { api, ApiError, setUnauthorizedHandler } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';

/**
 * Authentication state.
 *
 * There is no token here to store. The session lives in an HttpOnly cookie,
 * so "am I signed in?" is answered by asking the server (`/auth/me`), which
 * also means a revoked or expired session is detected on the next request
 * rather than trusted from client state.
 */

interface AuthContextValue {
  user: MeResponse | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me);
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        setUser(null);
        setStatus('anonymous');
        return;
      }
      // A network failure is not the same as being signed out; stay anonymous
      // but let individual views surface the connection error.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Any 401 from anywhere drops us to the login screen and clears caches. */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('anonymous');
      queryClient.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await api.post<{ user: MeResponse }>('/auth/login', { identifier, password });
    setUser(result.user);
    setStatus('authenticated');
    // Drop anything cached while anonymous.
    queryClient.clear();
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      setStatus('anonymous');
      queryClient.clear();
    }
  }, []);

  const can = useCallback(
    (permission: Permission) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout, can, refresh }),
    [user, status, login, logout, can, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
